// Hub HTTP API helpers (cookie-authenticated). See docs/accounts-api.md.

import type { Me, Settings } from './store';

export type Role = 'admin' | 'member';

export interface User {
  id: string;
  email: string;
  role: Role;
  machine: string | null;
  disabled: boolean;
  created: string;
  lastLogin: string | null;
}

export interface Machine {
  id: string;
  status: string;
  owner: string | null;
  vcpu?: number;
  memoryGB?: number;
  diskGB?: number;
  region?: string;
  tier?: string;
}

/** A machine size (the hub's tiers.go): its disk comes with it; prices in US cents. */
export interface Tier {
  id: string;
  name: string;
  kind: 'standard' | 'performance';
  vcpu: number;
  memoryGB: number;
  diskGB: number;
  monthly: number;
  yearly: number;
  available: boolean;
}

export type Interval = 'month' | 'year';

/** A size as Plans sells it (GET /api/plans, /api/billing). */
export type PlanOption = Omit<Tier, 'kind'>;

/** The account's plan, as the hub keeps it (billing_state.go view). Times in unix seconds. */
export interface Plan {
  tier: string;
  interval: Interval;
  status: string;
  periodEnd: number;
  cancelAtEnd: boolean;
  founding: boolean;
  /** A code's percent off, until offEnd (none: for good). */
  off?: number;
  offEnd?: number;
  unpaidSince?: number;
  pauseAt?: number;
  stoppedAt?: number;
  deleteAt?: number;
}

/** A discount code (a Stripe promotion code). */
export interface Code {
  id: string;
  code: string;
  percentOff: number;
  duration: 'once' | 'repeating' | 'forever';
  months?: number;
  tier?: string;
  maxUses?: number;
  used: number;
  expires?: number;
  active: boolean;
  created: number;
}

/** "$12", "$20.83": an amount in US cents. */
export const usd = (cents: number) => '$' + (cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2));

/** What a code does to a price, in words: "100% off for good", "50% off for 3 months". */
export function codeOff(c: Pick<Code, 'percentOff' | 'duration' | 'months'>) {
  const when = c.duration === 'forever' ? 'for good' : c.duration === 'once' ? 'the first payment' : `for ${c.months} months`;
  return `${c.percentOff}% off ${when}`;
}

export interface TierChoices {
  tiers: Tier[];
  disks: number[];
  default: string;
  current?: { tier: string; diskGB: number };
}

/** One usage sample (the agent's metrics.go). */
export interface UsageSample {
  t: number;
  cpu: number;
  cpus: number;
  memUsed: number;
  memTotal: number;
  netRx: number;
  netTx: number;
  diskUsed: number;
  diskTotal: number;
  diskRead: number;
  diskWrite: number;
  /** Swap in use and in all, bytes (no total, or 0: the machine has none). */
  swapUsed?: number;
  swapTotal?: number;
}

export type UsageRange = '1h' | '1d' | '7d';

export interface Usage {
  range: UsageRange;
  step: number;
  samples: UsageSample[] | null;
  latest: UsageSample | null;
}

export interface Dist {
  n: number;
  p50: number;
  p95: number;
  p99: number;
  max: number;
}

export interface TelemetrySummary {
  minutes?: number;
  echo: Dist;
  rtt: Dist;
  stallsOver300ms: number;
  echoByTransport?: Record<string, Dist>;
  /** By client: web, cli, mac, ios, windows. */
  echoByClient?: Record<string, Dist>;
  /** The native clients' echo by stage (in, wire, out, draw), by client. */
  stagesByClient?: Record<string, Record<string, Dist>>;
  /** Keystrokes whose echo never arrived inside the client's 2 s give-up (summed across clients). */
  echoLost?: number;
  /** Those never-echoed keystrokes, per client. */
  echoLostByClient?: Record<string, number>;
  /** End-to-end action timings (net/telemetry.ts recordOp), by action name. */
  opsByName?: Record<string, Dist>;
  /** Actions that failed, by action name. */
  opFailures?: Record<string, number>;
  /** Ops by client (web, cli, mac, ios, windows), by action name. */
  opsByClient?: Record<string, Record<string, Dist>>;
  /** Ops by app version, by action name. */
  opsByVersion?: Record<string, Record<string, Dist>>;
  /**
   * The clients' own process resources (the res object in a batch: windowSec, cpuPct, rssMB,
   * threads, fds/handles, framesPerSec, droppedFrames, mainThreadStallMs), as percentiles per
   * field. A client that can't measure its resources contributes nothing.
   */
  res?: Record<string, Dist>;
  /** Process resources by client (web, cli, mac, ios, windows), by field. */
  resByClient?: Record<string, Record<string, Dist>>;
  /** Process resources by app version, by field. */
  resByVersion?: Record<string, Record<string, Dist>>;
}

export interface WaitlistEntry {
  email: string;
  note: string;
  created: string;
}

export type ApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string; retryAfter?: number; body?: Record<string, unknown> };

/** JSON request helper. Never throws: network failures come back as status 0. */
/** What happens when the hub says this browser isn't signed in any more (session.ts: /api/me is
 * asked again, and the page goes to sign-in), so no panel is left showing "unauthorized". */
let signedOut = () => {};
export const whenSignedOut = (f: () => void) => {
  signedOut = f;
};

export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<ApiResult<T>> {
  let r: Response;
  try {
    r = await fetch(path, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      // State-changing requests must carry JSON content type (CSRF guard); the browser adds Origin.
      headers: method === 'GET' ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? (method === 'GET' ? undefined : '{}') : JSON.stringify(body),
    });
  } catch {
    return { ok: false, status: 0, error: 'Network error — check your connection and try again.' };
  }
  let json: Record<string, unknown> | undefined;
  try {
    const text = await r.text();
    json = text ? (JSON.parse(text) as Record<string, unknown>) : undefined;
  } catch {
    json = undefined;
  }
  if (r.ok) return { ok: true, status: r.status, data: (json ?? {}) as T };
  if (r.status === 401 && json?.error === 'unauthorized' && path !== '/api/me') signedOut();
  let retryAfter: number | undefined;
  const ra = json?.retryAfter ?? r.headers.get('retry-after');
  if (ra !== undefined && ra !== null && ra !== '' && Number.isFinite(Number(ra))) retryAfter = Number(ra);
  // The hub's errors are Go-style lowercase; on a page they start a sentence.
  const raw = typeof json?.error === 'string' && json.error ? (json.error as string) : `Request failed (HTTP ${r.status})`;
  const error = raw[0].toUpperCase() + raw.slice(1);
  return { ok: false, status: r.status, error, retryAfter, body: json };
}

/** Accepts both the accounts-API shape (flat User & {...}) and the legacy `{ user: {...} }` one. */
export function normalizeMe(raw: Record<string, unknown>): Me {
  const legacy = (raw.user && typeof raw.user === 'object' ? raw.user : {}) as Record<string, unknown>;
  const pick = (k: string) => raw[k] ?? legacy[k];
  const str = (v: unknown) => (typeof v === 'string' ? v : undefined);
  let machine: Me['machine'] = null;
  const m = raw.machine;
  if (m && typeof m === 'object') {
    const mm = m as Record<string, unknown>;
    machine = {
      id: String(mm.id ?? ''),
      status: String(mm.status ?? 'unknown'),
      owner: str(mm.owner) ?? null,
      vcpu: typeof mm.vcpu === 'number' ? mm.vcpu : undefined,
      memoryGB: typeof mm.memoryGB === 'number' ? mm.memoryGB : undefined,
      diskGB: typeof mm.diskGB === 'number' ? mm.diskGB : undefined,
      region: str(mm.region),
      tier: str(mm.tier),
    };
  } else if (typeof m === 'string' && m) {
    machine = { id: m, status: 'unknown', owner: null };
  }
  const role = pick('role') === 'admin' ? 'admin' : 'member';
  return {
    id: str(pick('id')) ?? '',
    email: str(pick('email')) ?? str(pick('login')) ?? str(pick('name')) ?? '',
    role,
    disabled: !!pick('disabled'),
    created: str(pick('created')) ?? null,
    lastLogin: str(pick('lastLogin')) ?? null,
    machine,
    baseDomain: str(raw.baseDomain),
    previewSuffix: str(raw.previewSuffix),
    scheme: str(raw.scheme),
    settings: normalizeSettings(raw.settings),
    journey: Array.isArray(raw.journey) ? raw.journey.filter((x): x is string => typeof x === 'string') : [],
    apps: normalizeApps(raw.apps),
    setup: normalizeSetup(raw.setup),
    billing: {
      enabled: !!(raw.billing as Record<string, unknown> | null)?.enabled,
      plan: ((raw.billing as Record<string, unknown> | null)?.plan as Plan | null) ?? null,
    },
  };
}

/** Plans are paid for here and this account has none (and no machine from before): Plans first. */
export const needsPlan = (me: Me | null | undefined) => !!me && me.billing.enabled && !me.billing.plan && !me.machine;

/** The apps published for download (hub journey.go apps); an older hub's missing ones are not. */
function normalizeApps(raw: unknown): Me['apps'] {
  const a = (raw ?? {}) as Record<string, unknown>;
  return { mac: !!a.mac, windows: !!a.windows, android: !!a.android };
}

/** Older hubs have no gate: set up, nothing required. */
function normalizeSetup(raw: unknown): Me['setup'] {
  const s = raw as Record<string, unknown> | null;
  if (!s || typeof s !== 'object') return { done: true, required: false, offer: 'done' };
  const offer = s.offer === 'ask' || s.offer === 'declined' ? s.offer : 'done';
  return { done: s.done !== false, required: s.required === true, offer };
}

/** The web waits for setup (hub gate.go): true when this person must finish it first. */
export const mustSetUp = (me: Me | null | undefined) => !!me && me.setup.required && !me.setup.done;

/** "Don't ask again" to Magic Setup, for every app (spec "Asking until it's done or declined"). */
export const declineSetup = () => api('POST', '/api/journey', { step: 'setup-declined' });

/** Reports the end of onboarding (the browser path on Getting ready). */
/** The one line that installs the cmx CLI from this hub (`/install`, macOS and Linux). */
export const installCommand = () => `curl -fsSL ${location.origin}/install | sh`;

export const reportOnboarded = () => api<{ ok: boolean; setup: { done: boolean; required: boolean } }>('POST', '/api/journey', { step: 'onboarded' });

function normalizeSettings(raw: unknown): Settings {
  const { links, organize, sessionPreview, phone, sound, fontSize, cursor } = (raw ?? {}) as Record<string, unknown>;
  return {
    links: links === 'device' || links === 'machine' ? links : 'auto',
    organize: organize === 'none' || organize === 'folder' ? organize : 'program',
    sessionPreview: sessionPreview === 'off' ? 'off' : 'on',
    ...(typeof phone === 'boolean' && { phone }),
    ...(typeof sound === 'boolean' && { sound }),
    ...(typeof fontSize === 'number' && { fontSize }),
    ...((cursor === 'block' || cursor === 'bar' || cursor === 'underline') && { cursor }),
  };
}

/** Saves settings (the ones given change, the rest stay); the saved settings, or an error message. */
export async function saveSettings(st: Partial<Settings>): Promise<{ ok: true; settings: Settings } | { ok: false; error: string }> {
  const r = await api<Record<string, unknown>>('PUT', '/api/settings', st);
  return r.ok ? { ok: true, settings: normalizeSettings(r.data) } : { ok: false, error: r.error };
}

export type MeResult = { ok: true; me: Me } | { ok: false; status: number; github: boolean; error?: string };

export async function fetchMe(): Promise<MeResult> {
  const r = await api<Record<string, unknown>>('GET', '/api/me');
  if (r.ok) return { ok: true, me: normalizeMe(r.data) };
  return { ok: false, status: r.status, github: !!r.body?.github, error: r.status === 0 ? r.error : undefined };
}

/** Legacy bootstrap-token login (hidden; tests and recovery). Returns an error message or null. */
export async function tokenLogin(token: string): Promise<string | null> {
  const r = await api('POST', '/api/login', { token });
  if (r.ok) return null;
  return r.status === 401 || r.status === 403 ? 'Invalid token' : r.error;
}

export const passwordLogin = (email: string, password: string) => api('POST', '/api/auth/login', { email, password });

export async function logout() {
  const r = await api('POST', '/api/auth/logout');
  // Older hubs only know the legacy endpoint.
  if (!r.ok && (r.status === 404 || r.status === 405)) await api('POST', '/api/logout');
}

export const changePassword = (current: string, next: string) => api('POST', '/api/auth/password', { current, next });
export const fetchTelemetry = (minutes: number) => api<TelemetrySummary>('GET', `/api/telemetry?minutes=${minutes}`);


/** unreachable: on, but cmx can't reach it or its host right now (it's still there). */
export type MachineLife = 'creating' | 'booting' | 'ready' | 'error' | 'stopped' | 'rebooting' | 'unreachable';
export interface MachineState {
  id: string;
  state: MachineLife;
  detail?: string;
}

// ---- signing up (hub signup.go, oauth.go) ----

/** How people sign in and up here: Google/GitHub when set up, and whether plans are paid for. */
/** signup: new accounts are taken (a hub with plans, or a dev hub). */
export const authMethods = () => api<{ oauth: ('google' | 'github')[]; billing: boolean; signup: boolean }>('GET', '/api/auth/methods');
/** An unconfirmed account and a code by email (devCode: dev hubs without email). */
export const signup = (email: string, password: string, invite?: string) =>
  api<{ verify: boolean; email: string; devCode?: string }>('POST', '/api/signup', { email, password, terms: true, invite });

/** An internal-tester invite link (spec "Internal testers"): can it sign someone up, to what size. */
export type InviteInfo = { valid: boolean; status: string; tier?: string; error?: string };
export const inviteInfo = (token: string) => api<InviteInfo>('GET', `/api/invites/${encodeURIComponent(token)}`);
export type Invite = {
  id: string;
  note: string;
  tier: string;
  region: string;
  created: string;
  expires: string;
  usedBy?: string;
  usedAt?: string;
  revoked: boolean;
  status: 'active' | 'claimed' | 'used' | 'expired' | 'revoked';
};
export const verifySignup = (email: string, code: string) => api<{ ok: boolean }>('POST', '/api/signup/verify', { email, code });
export const resendCode = (email: string) => api<{ ok: boolean; devCode?: string }>('POST', '/api/signup/resend', { email });
export const deleteAccount = (confirm: string) => api('DELETE', '/api/account', { confirm });

// ---- plans and payments (hub billing.go) ----

export const plans = () => api<{ plans: PlanOption[]; billing: boolean }>('GET', '/api/plans');
export const billing = {
  get: () => api<{ enabled: boolean; plans?: PlanOption[]; subscription: Plan | null }>('GET', '/api/billing'),
  code: (code: string) => api<{ code: Code }>('GET', `/api/billing/code?code=${encodeURIComponent(code)}`),
  checkout: (tier: string, interval: Interval, code?: string) =>
    api<{ url: string }>('POST', '/api/billing/checkout', code ? { tier, interval, code } : { tier, interval }),
  sync: (session: string) => api<{ subscription: Plan | null }>('POST', '/api/billing/sync', { session }),
  preview: (tier: string, interval: Interval) =>
    api<{ amountDue: number; total: number; currency: string }>('POST', '/api/billing/change', { tier, interval, preview: true }),
  change: (tier: string, interval: Interval) => api<{ subscription: Plan }>('POST', '/api/billing/change', { tier, interval }),
  portal: () => api<{ url: string }>('POST', '/api/billing/portal'),
};

/** Opens Stripe's billing page for this account (card, invoices, cancelling); an error message, or null. */
export async function openBillingPortal(): Promise<string | null> {
  const r = await billing.portal();
  if (!r.ok) return r.error;
  location.assign(r.data.url);
  return null;
}

// The code someone arrived with (/signup?code=), kept for Plans in this browser.
const CODE_KEY = 'cmx.code';
export function rememberCode(code: string | null) {
  try {
    if (code) sessionStorage.setItem(CODE_KEY, code.trim().toUpperCase());
  } catch {
    /* private window: they can type it on Plans */
  }
}
export function rememberedCode(): string {
  try {
    return sessionStorage.getItem(CODE_KEY) ?? '';
  } catch {
    return '';
  }
}
/** Forgot password: email a link (the answer is the same whether or not the account exists). */
export const forgotPassword = (email: string) => api('POST', '/api/auth/forgot', { email });
export interface ResetInfo {
  valid: boolean;
  email?: string;
  error?: string;
}
export const resetInfo = (token: string) => api<ResetInfo>('GET', `/api/auth/reset/${encodeURIComponent(token)}`);
export const resetPassword = (token: string, password: string) =>
  api<{ ok: boolean; email: string }>('POST', '/api/auth/reset', { token, password });
export const machineState = () => api<{ machine: MachineState }>('GET', '/api/machine');
export const createMachine = (tier?: string) => api<{ machine: MachineState }>('POST', '/api/machine', tier ? { tier } : undefined);
export const machineTiers = (id?: string) =>
  api<TierChoices>('GET', id ? `/api/admin/machines/${encodeURIComponent(id)}/tiers` : '/api/machine/tiers');
export const resizeMachine = (tier: string, id?: string) =>
  api<{ ok: boolean; applied?: { restarted?: boolean } }>(
    'POST',
    id ? `/api/admin/machines/${encodeURIComponent(id)}/resize` : '/api/machine/resize',
    { tier },
  );
export const rebootMachine = (resume: boolean, id?: string) =>
  api('POST', id ? `/api/admin/machines/${encodeURIComponent(id)}/reboot` : '/api/machine/reboot', { resume });

/** What "continue where I left off" would bring back after a reboot. */
export interface RestorePreview {
  sessions: number;
  agents: Record<string, number>;
  apps: number;
  tabs: number;
}
export const restorePreview = (id?: string) =>
  api<RestorePreview>('GET', id ? `/api/admin/machines/${encodeURIComponent(id)}/restore-preview` : '/api/machine/restore-preview');
export const machineUsage = (range: UsageRange, since = 0, id?: string) =>
  api<Usage>(
    'GET',
    `${id ? `/api/admin/machines/${encodeURIComponent(id)}/metrics` : '/api/machine/metrics'}?range=${range}&since=${since}`,
  );

/** GET /api/admin/host: the host as admission sees it (spec "Host capacity"). MB unless named. */
export type HostReport = {
  host: {
    cpus: number;
    memoryMB: number;
    availMB: number;
    minAvailMB: number;
    memPressure: number;
    cpuPressure: number;
    swapTotalMB: number;
    swapFreeMB: number;
  } | null;
  rules?: { CPUOverbook: number; MemOverbook: number; MemFloor: number; MemPressureMax: number; CPUPressureMax: number };
  booked?: { cpus: number; cpusMax: number; memGB: number; memGBMax: number; diskGB: number; diskGBMax: number };
  floorMB?: number;
  newMachine?: number;
  /** Why a new Standard M wouldn't fit; "" when it would. */
  full?: string;
  sweptAt?: number;
  machines?: { id: string; tier: string; heldMB: number; usedMB: number; freeMB: number; swapMB?: number }[] | null;
  /** The host's guard (hostd's guard.go): since when it finds the host short (unix; none: it isn't),
   * the machine its kernel would stop first, and its last steps. */
  shortSince?: number;
  blamed?: string;
  guard?: GuardEvent[] | null;
};

/** One step of the host's guard (hostd's GuardEvent), MB: a machine asked for its page cache
 * back, or given the memory it was made bigger by. */
export type GuardEvent = {
  at: number;
  id: string;
  /** reclaim | plug */
  what: string;
  drill?: boolean;
  why?: string;
  heldMB: number;
  availMB: number;
  cacheMB?: number;
  cacheLeftMB?: number;
  memoryMB?: number;
  error?: string;
};

export const admin = {
  host: () => api<HostReport>('GET', '/api/admin/host'),
  users: () => api<{ users: User[] }>('GET', '/api/admin/users'),
  createUser: (u: { email: string; password: string; role?: Role; machine?: string | null }) =>
    api<{ user: User }>('POST', '/api/admin/users', u),
  updateUser: (id: string, patch: Partial<{ password: string; role: Role; machine: string | null; disabled: boolean }>) =>
    api<{ user: User }>('PATCH', `/api/admin/users/${encodeURIComponent(id)}`, patch),
  deleteUser: (id: string) => api('DELETE', `/api/admin/users/${encodeURIComponent(id)}`),
  machines: () => api<{ machines: Machine[] }>('GET', '/api/admin/machines'),
  waitlist: () => api<{ entries: WaitlistEntry[] }>('GET', '/api/admin/waitlist'),
  removeWaitlist: (email: string) => api('DELETE', `/api/admin/waitlist/${encodeURIComponent(email)}`),
  codes: () => api<{ codes: Code[]; enabled: boolean }>('GET', '/api/admin/codes'),
  invites: () => api<{ invites: Invite[] }>('GET', '/api/admin/invites'),
  createInvite: (note: string, tier: string) => api<{ invite: Invite; url: string }>('POST', '/api/admin/invites', { note, tier }),
  revokeInvite: (id: string) => api<{ ok: boolean }>('DELETE', `/api/admin/invites/${encodeURIComponent(id)}`),
  createCode: (c: Partial<Code>) => api<{ code: Code; link: string }>('POST', '/api/admin/codes', c),
  deactivateCode: (id: string) => api('DELETE', `/api/admin/codes/${encodeURIComponent(id)}`),
  /** Founding plans: one account (user), or everyone with a machine from before launch. */
  founding: (user?: string) =>
    api<{ done: string[]; failed: Record<string, string> }>('POST', '/api/admin/billing/founding', user ? { user } : {}),
  deleteMachine: (id: string) => api('DELETE', `/api/admin/machines/${encodeURIComponent(id)}`),
  reports: () => api<{ reports: AppReport[] }>('GET', '/api/admin/reports'),
  report: (id: string) => api<AppReport>('GET', `/api/admin/reports/${encodeURIComponent(id)}`),
};

/** A crash or problem an app sent (internal/hub/reports.go); report and log only one at a time. */
export interface AppReport {
  id: string;
  type: 'crash' | 'problem';
  app: string;
  version: string;
  build: string;
  os: string;
  device: string;
  at: string;
  kind: string;
  reason: string;
  user: string;
  email: string;
  received: string;
  report?: string;
  log?: string;
}

/** A port's (or the desktop's) private preview link. */
export const previewHref = (port: number | 'desktop') => `/preview?port=${port}`;

export function openPreview(port: number | 'desktop') {
  window.open(previewHref(port), '_blank', 'noopener');
}

// ---- cmx CLI (device approval, CLI devices) ----

export interface CliDevice {
  id: string;
  name: string;
  /** 'app' for the Mac or iPhone app; absent for the cmx CLI */
  kind?: string;
  /** The app it is (the dashboard's apps row); absent for a file manager's link (Finder, File Explorer). */
  client?: 'mac' | 'windows' | 'iphone' | 'android' | 'cli';
  created: string;
  lastUsed: string | null;
}

export const cli = {
  code: (code: string) => api<{ code: string; name: string; expiresIn: number }>('GET', `/api/cli/code?code=${encodeURIComponent(code)}`),
  approve: (code: string) => api('POST', '/api/cli/approve', { code }),
  deny: (code: string) => api('POST', '/api/cli/deny', { code }),
  devices: () => api<{ devices: CliDevice[] }>('GET', '/api/cli/devices'),
  revoke: (id: string) => api('DELETE', `/api/cli/devices/${encodeURIComponent(id)}`),
};

/** A public link to a machine port (hub/share.go). */
export type Share = { id: string; url: string; port: number; expires: string; protected: boolean };
export const shares = {
  list: () => api<{ shares: Share[] }>('GET', '/api/shares'),
  create: (port: number, minutes: number, password?: string) => api<Share>('POST', '/api/shares', password ? { port, minutes, password } : { port, minutes }),
  revoke: (id: string) => api('DELETE', `/api/shares/${encodeURIComponent(id)}`),
};
